/**
 * API Service using the native Fetch API
 * Provides a consistent interface for all API calls to the backend
 * Handles CORS, authentication, error handling, and request/response interceptors
 */
import i18n from '../i18n';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

// Types
interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
  data?: any;
}

interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

// Error class for API errors - structured to match Axios error format
class ApiError extends Error {
  public status: number;
  public data: any;
  
  // Add Axios-compatible properties for backward compatibility
  public response?: {
    data: any;
    status: number;
    statusText: string;
    headers: Headers;
  };
  
  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    
    // Add Axios-like response object for compatibility with existing code
    if (status !== 0) {
      this.response = {
        data,
        status,
        statusText: message,
        headers: new Headers()
      };
    }
  }
}

/**
 * Parse the response based on Content-Type header
 */
async function parseResponse(response: Response): Promise<any> {
  // Handle empty responses (204 No Content)
  if (response.status === 204) return null;

  const contentType = response.headers.get('Content-Type') || '';
  
  if (contentType.includes('application/json')) {
    return await response.json();
  } else if (contentType.includes('text/')) {
    return await response.text();
  } else if (contentType.includes('form')) {
    return await response.formData();
  } else if (contentType.includes('video/') || contentType.includes('image/')) {
    return await response.blob();
  } else {
    return await response.text();
  }
}

/**
 * Process API response and handle errors
 */
async function processResponse<T>(response: Response): Promise<ApiResponse<T>> {
  // Connexion DB perdue : vider la session et rediriger sans laisser React afficher l'erreur
  if (response.status === 503 && !window.location.pathname.includes('/login')) {
    localStorage.removeItem('token');
    window.location.replace('/login');
    await new Promise(() => {}); // Suspend jusqu'à ce que la navigation remplace la page
  }

  // Token expiré/invalide : déconnecter proprement plutôt que de laisser
  // remonter le message brut du backend dans un composant quelconque.
  // On exclut /auth/login, dont le 401 signifie juste "mauvais mot de passe".
  if (
    response.status === 401 &&
    !response.url.includes('/auth/login') &&
    !window.location.pathname.includes('/login')
  ) {
    localStorage.removeItem('token');
    window.location.replace('/login?reason=session_expired');
    await new Promise(() => {}); // Suspend jusqu'à ce que la navigation remplace la page
  }

  // First handle HTTP error responses
  if (!response.ok) {
    const errorData = await parseResponse(response).catch(() => null);
    
    // Format error message from response if possible
    let errorMessage = 'Request failed';
    if (errorData?.detail) {
      if (Array.isArray(errorData.detail)) {
        errorMessage = errorData.detail.map((err: any) => err.msg || JSON.stringify(err)).join('. ');
      } else if (typeof errorData.detail === 'object' && 'msg' in errorData.detail) {
        errorMessage = errorData.detail.msg;
      } else if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      }
    } else if (errorData?.message) {
      errorMessage = errorData.message;
    }
    
    throw new ApiError(
      errorMessage,
      response.status,
      errorData
    );
  }
  
  // For successful responses, parse the data
  const data = await parseResponse(response);
  
  return {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers
  };
}

/**
 * Add query parameters to a URL
 */
function addParamsToUrl(url: string, params?: Record<string, string>): string {
  if (!params) return url;
  
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });
  
  const queryString = searchParams.toString();
  if (!queryString) return url;
  
  return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
}

/**
 * Create request headers including auth token if available
 */
function createHeaders(customHeaders?: HeadersInit): Headers {
  const headers = new Headers(customHeaders);
  
  // Set default headers if not provided
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  
  // Add auth token from localStorage if available
  const token = localStorage.getItem('token');
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Add current language from i18n
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', i18n.language || 'fr');
  }
  
  return headers;
}

/**
 * Core request function
 */
async function request<T = any>(
  url: string,
  method: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  // Create full URL with base and query parameters
  const fullUrl = addParamsToUrl(`${API_BASE_URL}${url}`, options.params);
  
  // Create headers
  const headers = createHeaders(options.headers);
  
  // Create request init object
  const requestInit: RequestInit = {
    method,
    headers,
    credentials: 'omit', // Never include credentials by default to avoid CORS issues
    mode: 'cors',
    ...options,
  };
  
  // Add body for methods that support it
  if (['POST', 'PUT', 'PATCH'].includes(method) && options.data !== undefined) {
    // Handle FormData differently
    if (options.data instanceof FormData) {
      // Don't set content-type for FormData to let browser set it with boundary
      headers.delete('Content-Type');
      requestInit.body = options.data;
    } else if (headers.get('Content-Type')?.includes('application/json')) {
      requestInit.body = JSON.stringify(options.data);
    } else {
      requestInit.body = options.data;
    }
  }
  
  try {
    const response = await fetch(fullUrl, requestInit);
    return await processResponse<T>(response);
  } catch (error) {
    console.error('Network error:', error);
    
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Create Axios-like error structure
    const apiError = new ApiError(
      (error as Error).message || 'Network Error',
      0  // 0 status indicates network error in Axios
    );
    
    throw apiError;
  }
}

/**
 * Specialized login function with Fetch API
 * Uses direct fetch call with specific settings to avoid CORS issues
 */
async function loginWithFetch(username: string, password: string): Promise<ApiResponse<{ access_token: string }>> {
  // Create form data for the request
  const formData = new FormData();
  formData.append('username', username);
  formData.append('password', password);
  
  try {
    // Direct fetch request with minimal headers and no credentials
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Accept-Language': i18n.language || 'fr'
        // Don't set Content-Type - browser will add it with appropriate boundary
      },
      body: formData,
      credentials: 'omit',
      mode: 'cors'
    });
    
    // Handle HTTP error responses
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { detail: await response.text() || 'Unknown error' };
      }
      
      throw new ApiError(
        errorData?.detail || 'Login failed',
        response.status,
        errorData
      );
    }
    
    // Parse successful response
    const data = await response.json();
    
    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    };
  } catch (error) {
    // If error is already an ApiError (from above), just rethrow it
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Otherwise, wrap in network error
    throw new ApiError(
      (error as Error).message || 'Network Error - Could not connect to the server',
      0
    );
  }
}

// API methods object
const api = {
  /**
   * HTTP GET request
   */
  get<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(url, 'GET', options);
  },
  
  /**
   * HTTP POST request
   */
  post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(url, 'POST', { ...options, data });
  },
  
  /**
   * HTTP PUT request
   */
  put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(url, 'PUT', { ...options, data });
  },
  
  /**
   * HTTP PATCH request
   */
  patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(url, 'PATCH', { ...options, data });
  },
  
  /**
   * HTTP DELETE request
   */
  delete<T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>(url, 'DELETE', options);
  },
  
  /**
   * Login method using Fetch API
   */
  login: async (username: string, password: string): Promise<ApiResponse<{ access_token: string }>> => {
    try {
      // Use specialized fetch approach for login
      return await loginWithFetch(username, password);
    } catch (error) {
      // If fetch fails due to CORS, the error is already in the correct format
      throw error;
    }
  }
};

// ---------------------------------------------------------------------------
// Suspension types
// ---------------------------------------------------------------------------

export interface SuspensionRead {
  id: number;
  sensor_id: number;
  start_at: string;
  end_at: string;
  created_by: number;
  created_at: string;
  deleted_by: number | null;
  deleted_at: string | null;
  state: 'active' | 'scheduled' | 'expired' | 'deleted';
  deletion_type: 'stopped' | 'cancelled' | 'interrupted' | null;
  is_indefinite: boolean;
  creator_name: string | null;
  deleter_name: string | null;
  sensor_name: string | null;
}

export interface SuspensionListResponse {
  items: SuspensionRead[];
  total: number;
  page: number;
  page_size: number;
}

export interface SuspensionSummary {
  active: number;
  scheduled: number;
}

export interface RecurrenceSpec {
  frequency: 'daily' | 'weekly';
  interval: number;
  weekdays?: number[];
  until?: string;
  count?: number;
}

export interface SuspensionCreate {
  sensor_id: number;
  start_at: string;
  end_at: string;
  recurrence?: RecurrenceSpec;
}

export interface SuspensionListParams {
  sensor_id?: number;
  state?: 'active' | 'scheduled' | 'expired' | 'deleted';
  deletion_type?: 'stopped' | 'cancelled' | 'interrupted';
  include_past?: boolean;
  date_from?: string;   // ISO 8601
  search?: string;
  sort_field?: string;
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

// ---------------------------------------------------------------------------
// Suspension API methods
// ---------------------------------------------------------------------------

export const suspensionApi = {
  list: (params?: SuspensionListParams) => {
    const query: Record<string, string> = {};
    if (params?.sensor_id !== undefined)   query.sensor_id     = String(params.sensor_id);
    if (params?.state)                     query.state         = params.state;
    if (params?.deletion_type)             query.deletion_type = params.deletion_type;
    if (params?.include_past)              query.include_past  = 'true';
    if (params?.date_from)                 query.date_from     = params.date_from;
    if (params?.search)                    query.search        = params.search;
    if (params?.sort_field)                query.sort_field    = params.sort_field;
    if (params?.sort_dir)                  query.sort_dir      = params.sort_dir;
    if (params?.page !== undefined)        query.page          = String(params.page);
    if (params?.page_size !== undefined)   query.page_size     = String(params.page_size);
    return api.get<SuspensionListResponse>('/suspensions/', { params: query });
  },

  get: (id: number) =>
    api.get<SuspensionRead>(`/suspensions/${id}`),

  create: (payload: SuspensionCreate) =>
    api.post<SuspensionRead[]>('/suspensions/', payload),

  delete: (id: number) =>
    api.delete(`/suspensions/${id}`),

  summary: () =>
    api.get<SuspensionSummary>('/suspensions/summary'),
};

export default api;